import {
  ScrollReveal,
  Label,
  Stat,
  Divider,
  Photo,
  ReportFooter,
  containerClassName,
} from "./ReportComponents";
import { PhotoGallery } from './PhotoGallery';
import BudmenLogo from '../shared/BudmenLogo';
import ConnectionsGame from '../games/ConnectionsGame';
import MemoryMatchGame from '../games/MemoryMatchGame';
import TriviaGame from '../games/TriviaGame';
import ReportGameWrapper from '../games/ReportGameWrapper';

// Styles
import '../../styles/reportStyles.css';
import '../../styles/reports.css';
import '../../styles/reportPrint.css';
import '../../styles/designTokens.css';

// ═══════════════════════════════════════════════════════════════════
// SUNY POLY REPORT COMPONENT
// ═══════════════════════════════════════════════════════════════════

export function SunyReport({ data }) {
  return (
    <div className="report-container">
      {/* HERO */}
      <div
        className="report-hero"
        style={data.heroImage ? {
          backgroundImage: `url(${data.heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        } : undefined}
      >
        {data.heroImage ? (
          <div className={`report-hero__overlay report-hero__overlay--${data.heroOverlay || 'dark'}`} />
        ) : (
          <div className="report-hero__pattern" />
        )}
        <div className="report-hero__content">
          <div className="report-hero__logo">
            <BudmenLogo fill="white" height="40" />
          </div>
          <div className="report-hero__main">
            <Label color="var(--gold)">Program Report</Label>
            <h1 className="report-hero__title">
              {data.program.name}
            </h1>
            <p className="report-hero__tagline">
              {data.program.tagline}
            </p>
          </div>
          <div className="report-hero__meta">
            <span>{data.program.partner}</span>
            <span>·</span>
            <span>{data.program.dates}</span>
          </div>
        </div>
      </div>

      {/* DRIVING QUESTION */}
      <ScrollReveal className="report-section--driving">
        <div className={containerClassName}>
          <Label>The Driving Question</Label>
          <blockquote className="report-blockquote">
            {data.drivingQuestion}
          </blockquote>
        </div>
      </ScrollReveal>

      {/* OVERVIEW */}
      <ScrollReveal className="report-section--overview">
        <div className={containerClassName}>
          <p className="report-text">
            {data.overview}
          </p>
        </div>
      </ScrollReveal>

      {/* STATS */}
      <ScrollReveal className="report-stats">
        <div className="report-stats__container">
          {data.customStats ? (
            data.customStats.map((stat, index) => (
              <Stat key={index} number={stat.number} label={stat.label} />
            ))
          ) : (
            <>
              <Stat number={data.program.studentsServed} label="Students" />
              <Stat number={data.program.daysDelivered} label="Days" />
              <Stat number="6" label="Supporting Adults" />
              <Stat number="1" label="Intuitive Fixture Maker" />
            </>
          )}
        </div>
      </ScrollReveal>

      <Divider />

      {/* DAY BY DAY */}
      <ScrollReveal className="report-section--tight">
        <div className={containerClassName}>
          <Label>Day by Day</Label>
          <h2 className="report-h2">
            Three Days of Discovery
          </h2>

          {data.dayByDayIntro && (
            <p className="report-text">
              {data.dayByDayIntro}
            </p>
          )}

          <div className="day-sections">
            {data.dayByDay.map((day, i) => (
              <ScrollReveal key={day.day} delay={i * 100}>
                <div className="day-section">
                  <div className="day-section__header">
                    <div className="day-section__label">
                      DAY {day.day}
                    </div>
                    <span className="day-section__date">
                      {day.date}
                    </span>
                  </div>
                  <h3 className="day-section__title">
                    {day.title}
                  </h3>
                  <p className="day-section__content">
                    {day.content}
                  </p>
                  <div className="day-section__key-moment">
                    <span className="day-section__key-label">
                      Key Moment
                    </span>
                    <p className="day-section__key-text">
                      {day.keyMoment}
                    </p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>

          {/* Connections Game - comes after reading the day-by-day */}
          {data.connectionsGame && (
            <ReportGameWrapper
              showProgress={false}
              theme={{
                primaryColor: '#1B2A4A',
                secondaryColor: '#F7C948',
                borderColor: '#e5e7eb'
              }}
            >
              <ConnectionsGame
                title="Discover the Connections"
                instructions="Sort these sixteen items into four groups"
                data={data.connectionsGame}
              />
            </ReportGameWrapper>
          )}
        </div>
      </ScrollReveal>

      {/* PHOTOS */}
      <ScrollReveal className="report-section--overview photo-section">
        <div className={containerClassName}>
          <Label>In the Room</Label>
          {data.images && data.images.length > 0 ? (
            <PhotoGallery images={data.images} />
          ) : (
            <div className="photo-grid">
              <Photo caption="Students assembling Budmen Scribblers on Day 1" />
              <Photo caption="The cheese whiz dragon — a moment of glorious failure" />
              <Photo caption="Clay concepts alongside their 3D-printed twins on Day 3" />
            </div>
          )}
        </div>
      </ScrollReveal>

      <Divider />

      {/* FACILITATOR OBSERVATIONS */}
      <ScrollReveal className="report-section--tight">
        <div className={containerClassName}>
          <Label>Facilitator Observations</Label>
          <h2 className="report-h2">
            Program Insights
          </h2>

          {data.facilitatorObservationsIntro && (
            <p className="report-text">
              {data.facilitatorObservationsIntro}
            </p>
          )}

          {/* Facilitator Observations - show content first */}
          {data.facilitatorObservations && (
            <div className="observations">
              {data.facilitatorObservations.map((obs, i) => (
                <ScrollReveal key={i} delay={i * 70}>
                  <div className="observation-card">
                    <h3 className="observation-card__theme">
                      {obs.theme}
                    </h3>
                    <p className="observation-card__text">
                      {obs.observation}
                    </p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          )}

          {/* Trivia Game - comes after reading the observations */}
          {data.triviaGame && (
            <ReportGameWrapper
              showProgress={false}
              theme={{
                primaryColor: '#F5A623',
                secondaryColor: '#1B2A4A',
                borderColor: '#e5e7eb'
              }}
            >
              <TriviaGame
                title="Test Your Knowledge"
                instructions="See how much you picked up"
                data={data.triviaGame}
              />
            </ReportGameWrapper>
          )}
        </div>
      </ScrollReveal>

      <Divider />

      {/* OUTCOMES */}
      <ScrollReveal className="report-section--tight">
        <div className={containerClassName}>
          <Label>Outcomes</Label>
          <h2 className="report-h2">
            Student Capabilities
          </h2>

          {data.outcomesIntro && (
            <p className="report-text">
              {data.outcomesIntro}
            </p>
          )}

          {/* Outcomes List - show content first */}
          {data.outcomes && (
            <div className="outcomes">
              {data.outcomes.map((outcome, i) => (
                <ScrollReveal key={i} delay={i * 40}>
                  <div className="outcome">
                    <div className="outcome__bullet" />
                    <p className="outcome__text">
                      {outcome}
                    </p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          )}

          {/* Memory Match Game - comes after reading the outcomes */}
          {data.memoryMatchGame && (
            <ReportGameWrapper
              showProgress={false}
              theme={{
                primaryColor: '#C4652A',
                secondaryColor: '#F7C948',
                borderColor: '#e5e7eb'
              }}
            >
              <MemoryMatchGame
                title="Match the Outcomes"
                instructions="Connect each outcome to its source"
                data={data.memoryMatchGame}
              />
            </ReportGameWrapper>
          )}
        </div>
      </ScrollReveal>

      <Divider />

      {/* WHAT'S NEXT */}
      <ScrollReveal className="report-section--tight">
        <div className={containerClassName}>
          <Label>Looking Ahead</Label>
          <h2 className="report-h2">
            {data.lookingAhead ? data.lookingAhead.intro : 'Next Steps'}
          </h2>
          {data.lookingAhead ? (
            <div className="looking-ahead__actions">
              {data.lookingAhead.actions.map((action, i) => (
                <p key={i} className="report-text" style={{ marginBottom: '1.5rem' }}>
                  {action}
                </p>
              ))}
            </div>
          ) : (
            <p className="report-text">
              {data.whatsNext}
            </p>
          )}
        </div>
      </ScrollReveal>

      {/* FOOTER */}
      <ReportFooter reportDate={data.program.reportDate} partner={data.program.partner} />
    </div>
  );
}